import clsx from 'clsx'

import { Skeleton } from '@/shared/ui/Skeleton'

export type PartnerShowSkeletonProps = {
  className?: string
}

/**
 * Placeholder do perfil do parceiro, espelhando a anatomia real do show: card
 * de identidade, par de ações de contato, card de recompensa/check-in, bloco
 * sobre, catálogo e tags.
 *
 * Existe porque o `LoadWrapper` sem `skeleton` cai no `LoadingAlert` — um
 * "Carregando..." solto numa página que abre cheia de conteúdo, com o layout
 * saltando quando o registro chega.
 *
 * Sem capa de propósito: só o perfil premium tem capa, e enquanto o registro
 * não chega não dá para saber qual dos dois layouts virá — desenhar a capa
 * acertaria um caso e criaria salto no outro.
 */
export const PartnerShowSkeleton = ({
  className
}: PartnerShowSkeletonProps) => (
  <div
    className={clsx('pb-8', className)}
    aria-busy="true"
    aria-label="Carregando perfil do parceiro"
  >
    <div className="flex flex-col gap-5">
      {/* Identidade — logo 64px, nome, headline e tiles de redes sociais. */}
      <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(76,29,149,.07)]">
        <div className="flex items-start gap-3">
          <Skeleton variant="card" className="size-16 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
            <Skeleton className="h-5 w-[62%]" />
            <Skeleton className="h-3.5 w-[85%]" />
          </div>
        </div>

        <div className="mt-3.5 flex gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} variant="thumb" className="size-10" />
          ))}
        </div>
      </div>

      {/* Ações de contato — WhatsApp + CTA, lado a lado. */}
      <div className="flex gap-2.5">
        <Skeleton className="h-12 flex-1 rounded-[10px]" />
        <Skeleton className="h-12 flex-1 rounded-[10px]" />
      </div>

      {/* Recompensa + check-in. */}
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(76,29,149,.07)]">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton variant="pill" className="h-6 w-20" />
        </div>
        <Skeleton className="h-[30px] w-full rounded-[10px]" />
      </div>

      {/* Sobre. */}
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(76,29,149,.07)]">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[72%]" />
      </div>

      {/* Catálogo — faixa horizontal de itens. */}
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-28" />
        <div className="flex gap-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="flex w-1/2 flex-col gap-2">
              <Skeleton variant="card" className="h-24 w-full" />
              <Skeleton className="h-3 w-[80%]" />
            </div>
          ))}
        </div>
      </div>

      {/* Tags de networking. */}
      <div className="flex flex-wrap gap-2">
        {['w-20', 'w-16', 'w-24'].map(width => (
          <Skeleton key={width} variant="pill" className={clsx('h-6', width)} />
        ))}
      </div>
    </div>
  </div>
)
